import NetInfo from '@react-native-community/netinfo'
import { useEffect, useRef, useState } from 'react'
import { PB_SERVER_ADDR } from '@/lib/config'
import {
    HEALTH_TIMEOUT_MS,
    OFFLINE_DEBOUNCE_MS,
    ONLINE_DEBOUNCE_MS,
} from '@/lib/connectivity-timing'
import { log } from '@/lib/logger'

type Timer = ReturnType<typeof setTimeout> | null
type TimerRef = React.RefObject<Timer>

async function isServerReachable(): Promise<boolean> {
    try {
        const response = await fetch(`${PB_SERVER_ADDR}/api/health`, {
            method: 'HEAD',
            signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS),
        })
        return response.ok
    } catch {
        return false
    }
}

function clearTimer(ref: TimerRef) {
    if (ref.current) {
        clearTimeout(ref.current)
        ref.current = null
    }
}

function scheduleOnline(onlineTimer: TimerRef, setIsConnected: (v: boolean) => void) {
    if (onlineTimer.current) return
    onlineTimer.current = setTimeout(() => {
        onlineTimer.current = null
        setIsConnected(true)
    }, ONLINE_DEBOUNCE_MS)
}

function scheduleOffline(offlineTimer: TimerRef, setIsConnected: (v: boolean) => void) {
    if (offlineTimer.current) return
    offlineTimer.current = setTimeout(async () => {
        offlineTimer.current = null
        const reachable = await isServerReachable()
        log.debug('[connectivity] health check reachable:', reachable)
        if (!reachable) {
            setIsConnected(false)
        }
    }, OFFLINE_DEBOUNCE_MS)
}

export function useConnectivityMonitor() {
    const [isConnected, setIsConnected] = useState<boolean | null>(true)
    const offlineTimer = useRef<Timer>(null)
    const onlineTimer = useRef<Timer>(null)

    useEffect(() => {
        const unsubscribe = NetInfo.addEventListener(state => {
            const hasInternet = state.isConnected && state.isInternetReachable !== false
            log.debug('[connectivity]', {
                type: state.type,
                isConnected: state.isConnected,
                isInternetReachable: state.isInternetReachable,
                hasInternet,
            })

            if (hasInternet) {
                clearTimer(offlineTimer)
                scheduleOnline(onlineTimer, setIsConnected)
            } else {
                clearTimer(onlineTimer)
                scheduleOffline(offlineTimer, setIsConnected)
            }
        })

        return () => {
            unsubscribe()
            clearTimer(offlineTimer)
            clearTimer(onlineTimer)
        }
    }, [])

    return { isConnected }
}
